import React from 'react';

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
  size = 'default', // 'sm', 'default', 'lg'
  showInfo = false,
  totalItems = 0,
  itemsPerPage = 10,
  disabled = false
}) => {
  // Don't render if there are no pages
  if (totalPages < 1) return null;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !disabled) {
      onPageChange(page);
    }
  };

  const generatePageNumbers = () => {
    const pages = [];
    
    if (totalPages <= 7) {
      // If 7 or fewer pages, show all
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first 3 pages
      pages.push(1, 2, 3);
      
      // Add dots if there's a gap
      if (currentPage > 5) {
        pages.push('dots1');
      }
      
      // Add current page and surrounding pages if not already included
      if (currentPage > 3 && currentPage <= totalPages - 3) {
        if (currentPage > 4) {
          pages.push(currentPage - 1);
        }
        if (!pages.includes(currentPage)) {
          pages.push(currentPage);
        }
        if (currentPage < totalPages - 3) {
          pages.push(currentPage + 1);
        }
      }
      
      // Add dots if there's a gap before last 3
      if (currentPage < totalPages - 4) {
        pages.push('dots2');
      }
      
      // Always show last 3 pages
      if (totalPages > 3) {
        const lastThree = [totalPages - 2, totalPages - 1, totalPages];
        lastThree.forEach(page => {
          if (!pages.includes(page)) {
            pages.push(page);
          }
        });
      }
    }
    
    return pages;
  };

  const pageNumbers = generatePageNumbers();
  
  const getSizeClass = () => {
    switch (size) {
      case 'sm': return 'pagination-sm';
      case 'lg': return 'pagination-lg';
      default: return '';
    }
  };

  const getItemRange = () => {
    const start = Math.max(1, (currentPage - 1) * itemsPerPage + 1);
    const end = Math.min(currentPage * itemsPerPage, totalItems);
    return { start, end };
  };

  return (
    <div className={`d-flex justify-content-between align-items-center ${className}`}>
      {showInfo && totalItems > 0 && (
        <div className="text-muted">
          {(() => {
            const { start, end } = getItemRange();
            return (
              <>
                Showing <span className="fw-bold">{start}</span> to{' '}
                <span className="fw-bold">{end}</span> of{' '}
                <span className="fw-bold">{totalItems}</span> entries
              </>
            );
          })()}
        </div>
      )}
      
      <nav aria-label="Pagination Navigation">
        <ul className={`pagination mb-0 ${getSizeClass()}`}>
          {/* First Page Button */}
          <li className={`page-item ${currentPage === 1 || disabled ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1 || disabled}
              aria-label="Go to first page"
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
          </li>
          
          {/* Previous Button */}
          <li className={`page-item ${currentPage === 1 || disabled ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || disabled}
              aria-label="Go to previous page"
            >
              <i className="fas fa-angle-left"></i>
            </button>
          </li>

          {/* Page Numbers */}
          {pageNumbers.map((page, index) => {
            if (typeof page === 'string' && page.startsWith('dots')) {
              return (
                <li key={page} className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              );
            }
            
            return (
              <li
                key={page}
                className={`page-item ${currentPage === page ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page)}
                  disabled={disabled}
                  aria-label={`Go to page ${page}`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </button>
              </li>
            );
          })}

          {/* Next Button */}
          <li className={`page-item ${currentPage === totalPages || disabled ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || disabled}
              aria-label="Go to next page"
            >
              <i className="fas fa-angle-right"></i>
            </button>
          </li>
          
          {/* Last Page Button */}
          <li className={`page-item ${currentPage === totalPages || disabled ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || disabled}
              aria-label="Go to last page"
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default Pagination;